import api from "./api.js";
import { httpErrorMessage } from "../utils/httpErrorMessage.js";

export const createSubscriptionWithStripe = async (subscriptionData) => {
  try {
    const normalizedPayload = {
      planId: subscriptionData.planId,
      paymentMethodId: subscriptionData.paymentMethodId,
      customerEmail: subscriptionData.customerEmail,
      customerName: subscriptionData.customerName,
      productName: subscriptionData.productName,
      product: subscriptionData.product,
      projectId: subscriptionData.projectId,
      projectTier: subscriptionData.projectTier,
      subscriptionPlan: subscriptionData.subscriptionPlan,
      fullName: subscriptionData.fullName,
      emailAddress: subscriptionData.emailAddress,
      billingAddress: subscriptionData.billingAddress,
      city: subscriptionData.city,
      stateRegion: subscriptionData.stateRegion,
      country: subscriptionData.country,
      phone: subscriptionData.phone,
      amount: subscriptionData.amount,
      currency: subscriptionData.currency || "RON",
      websiteType: subscriptionData.websiteType,
      status: subscriptionData.status || "active",
      autoRenew: subscriptionData.autoRenew !== false,
      isUpdating: subscriptionData.isUpdating || false,
      existingPlanId: subscriptionData.existingPlanId,
      existingStripeSubscriptionId:
        subscriptionData.existingStripeSubscriptionId,
    };

    const response = await api.post(
      "/subscription-payments",
      normalizedPayload
    );

    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to create subscription"),
    };
  }
};

export const getSubscriptionPayments = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    if (filters.clientEmail) params.append("clientEmail", filters.clientEmail);
    if (filters.projectId) params.append("projectId", filters.projectId);
    if (filters.subscriptionPlanId)
      params.append("subscriptionPlanId", filters.subscriptionPlanId);

    const response = await api.get(
      `/subscription-payments?${params.toString()}`
    );

    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to get subscription payments"),
    };
  }
};

export const getSubscriptionPaymentById = async (paymentId) => {
  try {
    const response = await api.get(`/subscription-payments/${paymentId}`);

    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to get subscription payment"),
    };
  }
};

export const getSubscriptionPlanById = async (planId) => {
  try {
    const response = await api.get(`/subscription-plans/${planId}`);

    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to get subscription plan"),
    };
  }
};

export const getCompleteSubscriptionData = async (planId) => {
  try {
    let planData = null;
    try {
      const planResponse = await api.get(`/subscription-plans/${planId}`);
      if (planResponse.data?.success && planResponse.data?.data) {
        planData = planResponse.data.data;
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status && status !== 401 && status !== 403 && status !== 404) {
        throw err;
      }
    }

    if (!planData) {
      const myPlansResponse = await api.get("/subscription-plans/my-plans");
      const plans = myPlansResponse.data?.data;
      if (!myPlansResponse.data?.success || !Array.isArray(plans)) {
        throw new Error("Subscription plan not found");
      }

      planData =
        plans.find(
          (p) =>
            p?._id === planId ||
            p?.id === planId ||
            String(p?.projectId ?? "") === String(planId)
        ) || null;
    }

    if (!planData) {
      throw new Error("Subscription plan not found");
    }

    const paymentResponse = await api.get(
      `/subscription-payments?projectId=${encodeURIComponent(planData.projectId)}`
    );

    let paymentData = null;
    const payments =
      paymentResponse.data.success &&
      Array.isArray(paymentResponse.data.data) &&
      paymentResponse.data.data.length > 0
        ? paymentResponse.data.data
        : [];

    const planStripeId = String(planData?.stripeSubscriptionId || "").trim();
    if (payments.length > 0) {
      if (planStripeId) {
        paymentData =
          payments.find(
            (p) => String(p?.stripeSubscriptionId || "") === planStripeId
          ) || null;
      } else {
        paymentData = payments[0];
      }
    }
    if (!paymentData && planStripeId) {
      try {
        const bySub = await api.get(
          `/subscription-payments?stripeSubscriptionId=${encodeURIComponent(planStripeId)}`
        );
        const rows =
          bySub.data?.success && Array.isArray(bySub.data.data)
            ? bySub.data.data
            : [];
        if (rows.length > 0) paymentData = rows[0];
      } catch {
        /* ignore */
      }
    }

    const resolvedStripeId =
      planStripeId || String(paymentData?.stripeSubscriptionId || "").trim() || null;

    let cancelAtPeriodEnd = Boolean(planData?.cancelAtPeriodEnd);
    if (paymentData != null) {
      if (
        planStripeId &&
        String(paymentData.stripeSubscriptionId || "") === planStripeId
      ) {
        cancelAtPeriodEnd = Boolean(paymentData.cancelAtPeriodEnd);
      } else if (!planStripeId) {
        cancelAtPeriodEnd = Boolean(paymentData.cancelAtPeriodEnd);
      }
    }

    const combinedData = {
      ...planData,
      stripeSubscriptionId:
        resolvedStripeId || planData?.stripeSubscriptionId || paymentData?.stripeSubscriptionId || null,
      stripeCustomerId:
        planData?.stripeCustomerId || paymentData?.stripeCustomerId || null,
      stripePriceId: planData?.stripePriceId || paymentData?.stripePriceId || null,
      cancelAtPeriodEnd,
    };

    return {
      success: true,
      data: combinedData,
    };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to get complete subscription data"),
    };
  }
};

export const resumeSubscriptionRenewal = async ({ subscriptionId }) => {
  try {
    const response = await api.post("/subscription-payments/resume-renewal", {
      subscriptionId,
    });

    return {
      success: true,
      data: response.data?.data,
    };
  } catch (error) {
    return {
      success: false,
      error: httpErrorMessage(error, "Failed to resume renewal"),
    };
  }
};

export const cancelSubscriptionWithStripe = async (cancellationData) => {
  try {
    const normalizedPayload = {
      subscriptionId: cancellationData.subscriptionId,
      reason: cancellationData.reason || "User requested cancellation",
      feedback: cancellationData.feedback || "",
      cancelImmediately: cancellationData.cancelImmediately || false,
    };
    if (cancellationData.existingPlanId) {
      normalizedPayload.existingPlanId = cancellationData.existingPlanId;
    }

    const response = await api.post(
      "/subscription-payments/cancel",
      normalizedPayload
    );

    return {
      success: true,
      data: response.data.data,
    };
  } catch (error) {
    const payload = error?.response?.data;
    return {
      success: false,
      error:
        payload?.message ||
        error?.message ||
        "Failed to cancel subscription",
      code: payload?.code,
      data: payload?.data,
    };
  }
};

const subscriptionPaymentService = {
  createSubscriptionWithStripe,
  getSubscriptionPayments,
  getSubscriptionPaymentById,
  getSubscriptionPlanById,
  getCompleteSubscriptionData,
  resumeSubscriptionRenewal,
  cancelSubscriptionWithStripe,
};

export default subscriptionPaymentService;
